import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { NavLink, useNavigate } from 'react-router-dom'

import Input from '../../components/Input/Input'
import Button from '../../components/Button/Button'
import Calendar from '../../components/Calendar/Calendar'
import Dropdown from '../../components/Dropdown/Dropdown'
import ContainerPage from '../../components/ContainerPage/ContainerPage'
import PageTitle from '../../components/PageTitle/PageTitle'
import { showSnackBar } from '../../components/Snackbar/Snackbar'
import { useMyContextLoading } from '../../components/LoadingContextProvider/LoadingContextProvider'
import { areFieldsNotEmpty } from '../../helpers/helper'
import { phoneNumber } from '../../utils/payment'
import { dateToServer, isRealDate } from '../../utils/date'
import { createNewAffiliate, fetchTeamOffices } from '../../features/affiliates/affiliates'

const EMPTY_PERSON = {
  firstName: '',
  lastName: '',
  email: '',
  primaryPhone: '',
  address: { address: '', aptNumber: '', city: '', state: '', zip: '' },
  dateOfBirth: '',
}

const AddTeamMember = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { triggerLoading } = useMyContextLoading()
  const [offices, setOffices] = useState(null)
  const [officeId, setOfficeId] = useState('')
  const [person, setPerson] = useState(EMPTY_PERSON)

  useEffect(() => {
    dispatch(fetchTeamOffices()).then((response) => {
      const list = response.error ? [] : response.payload?.data || []
      if (response.error) showSnackBar('error', response.payload || 'Failed to load your offices')
      setOffices(list)
      if (list.length === 1) setOfficeId(list[0]._id)
    })
  }, [dispatch])

  const setField = (name, value) => {
    const [parent, child] = name.split('.')
    setPerson((previous) => (child
      ? { ...previous, [parent]: { ...previous[parent], [child]: value } }
      : { ...previous, [name]: name === 'primaryPhone' ? phoneNumber(value) : value }))
  }

  const canSave = Boolean(officeId)
    && areFieldsNotEmpty([person.firstName, person.lastName, person.email])
    && isRealDate(person.dateOfBirth)

  const handleSave = async () => {
    triggerLoading(true)
    const response = await dispatch(createNewAffiliate({
      userData: {
        ...person,
        primaryPhone: person.primaryPhone ? person.primaryPhone.replace(/\D/g, '') : person.primaryPhone,
        dateOfBirth: dateToServer(person.dateOfBirth),
        contactGroups: [officeId],
      },
    }))
    triggerLoading(false)

    if (response.error) {
      showSnackBar('error', response.payload || 'Something went wrong')
      return
    }

    showSnackBar('success', 'Invitation sent')
    navigate('/my-team')
  }

  const input = (name, label, placeholder, type) => (
    <Input
      type={type}
      width="w-full"
      placeholder={placeholder}
      label={label}
      value={name.split('.').reduce((value, key) => value?.[key], person)}
      onChange={(event) => setField(name, event.target.value)}
    />
  )

  return (
    <ContainerPage>
      <div className="flex justify-between items-center">
        <PageTitle title="Add team member" backTo="/my-team" />
      </div>
      <div className="flex w-full items-center justify-center">
        <div className="w-full max-w-[850px] rounded-2xl bg-surface border border-divider shadow-card p-12">
          <div className="pb-6 mb-6 border-b border-divider">
            {offices?.length === 0 && (
              <div className="text-text-secondary">You have no office to add people to.</div>
            )}
            {offices?.length === 1 && (
              <div>
                <div className="text-[13px] text-text-secondary">Joins office</div>
                <div className="text-text-primary">{offices[0].name}</div>
              </div>
            )}
            {offices?.length > 1 && (
              <Dropdown
                value={officeId}
                setValue={setOfficeId}
                menuItems={offices.map((office) => ({ value: office._id, label: office.name }))}
                className="w-full"
                variant="standard"
                placeholder="Choose an office"
                label="Office *"
              />
            )}
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-6 pb-6 h-max border-b border-divider">
            {input('firstName', 'First Name *', 'Enter First Name')}
            {input('lastName', 'Last Name *', 'Enter Last Name')}
            {input('email', 'Email *', 'Enter Email', 'email')}
            {input('primaryPhone', 'Primary Phone', 'Enter Primary Phone')}
            {input('address.address', 'Address', 'Enter Address')}
            {input('address.aptNumber', 'Suite/Apt#', '34')}
            {input('address.city', 'City', 'Enter City')}
            {input('address.state', 'State', 'Enter State')}
            {input('address.zip', 'Zip', 'Enter Zip')}
            <Calendar
              width="w-full"
              label="Date Of Birth *"
              height="56px"
              optional
              value={person.dateOfBirth}
              onChange={(value) => setField('dateOfBirth', value)}
            />
          </div>
          <div className="mt-6 flex justify-end space-x-4">
            <NavLink to="/my-team">
              <Button classes="mr-4" type="outlined" size="lg" width="min-w-[156px]">Close</Button>
            </NavLink>
            <Button onClick={handleSave} size="lg" width="min-w-[156px]" disabled={!canSave} classes="rounded-lg">
              Save
            </Button>
          </div>
        </div>
      </div>
    </ContainerPage>
  )
}

export default AddTeamMember
